import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip as MuiTooltip, Box } from '@mui/material';
import useStyles from './InfoTooltipStyles';

const InfoTooltip = ({
  children,
  text,
  placement = 'top',
  anchorOrigin,
  transformOrigin,
  offsetX = 0,
  offsetY = 0,
}) => {
  const classes = useStyles();

  const tooltipContent = (
    <Box className={classes.tooltipContent}>
      <Box className={classes.tooltipIcon}>i</Box>
      <Box className={classes.tooltipText}>{text}</Box>
    </Box>
  );

  const tooltipProps = {
    title: tooltipContent,
    placement,
    enterTouchDelay: 0,
    enterDelay: 100,
    leaveDelay: 200,
    leaveTouchDelay: 10000, // Keep tooltip open longer on mobile/touch devices
    disableHoverListener: false,
    disableTouchListener: false,
    disableFocusListener: true,
    disableInteractive: false,
    ...(anchorOrigin && { anchorOrigin }),
    ...(transformOrigin && { transformOrigin }),
    PopperProps: {
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [offsetX, offsetY],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            padding: 24,
          },
        },
      ],
      style: {
        pointerEvents: 'auto',
      },
    },
    componentsProps: {
      tooltip: {
        sx: {
          padding: 0,
          maxWidth: 'none',
          backgroundColor: 'transparent',
          pointerEvents: 'auto',
        },
      },
    },
  };

  // wrapper component to prevent prop forwarding to DOM elements and fix log warnings in console
  const TriggerWrapper = React.forwardRef((props, ref) => {
    // Filter out any props that shouldn't be on DOM elements
    const { anchorOrigin, transformOrigin, ...domProps } = props;
    return (
      <Box ref={ref} component='span' style={{ display: 'inline-block' }} {...domProps}>
        {children || <Box className={classes.iconCircle}>i</Box>}
      </Box>
    );
  });

  TriggerWrapper.displayName = 'TriggerWrapper';

  return (
    <MuiTooltip {...tooltipProps}>
      <TriggerWrapper />
    </MuiTooltip>
  );
};

InfoTooltip.propTypes = {
  children: PropTypes.node,
  text: PropTypes.string.isRequired,
  placement: PropTypes.oneOf([
    'bottom-end',
    'bottom-start',
    'bottom',
    'left-end',
    'left-start',
    'left',
    'right-end',
    'right-start',
    'right',
    'top-end',
    'top-start',
    'top',
  ]),
  anchorOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'center', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right']),
  }),
  transformOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'center', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right']),
  }),
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
};

export default InfoTooltip;
